import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  TextField,
  Box,
  useMediaQuery,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";

interface Slot {
  id: number;
  start_time: string;
  end_time: string;
  max_players: number;
  booked_players: number;
  available_players: number;
  isAvailable: boolean;
}

interface GameSlotModalProps {
  product: any; // IProductPopUP
  onClose: () => void;
  onConfirm: (slotSelection: { slotIds: number[]; quantity: number; date: string; slotTimes:string[]  }) => void;
}

const GameSlotModal: React.FC<GameSlotModalProps> = ({ product, onClose, onConfirm }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const API_URL = process.env.REACT_APP_API_URL;

  const [slots, setSlots] = useState<Slot[]>([]);
  const [filteredSlots, setFilteredSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [requestedPlayers, setRequestedPlayers] = useState<number>(1);
  const [selectedSlots, setSelectedSlots] = useState<number[]>([]);

  // Fetch slots from API
  useEffect(() => {
    if (!product?.id || !selectedDate || requestedPlayers < 1) return;

    const fetchAvailability = async () => {
      try {
        setLoading(true);

        const response = await axios.get(
          `${API_URL}/game-slots/${product.id}`,
          {
            params: {
              date: selectedDate,
              players: requestedPlayers,
            },
            headers: {
              Authorization: `Bearer ${
                localStorage.getItem("token") || ""
              }`,
            },
          }
        );

        setFilteredSlots(response.data?.data || []);
        setSelectedSlots([]);
        console.log(response.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch availability", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [selectedDate, requestedPlayers, product]);

  const toggleSlotSelection = (slotId: number) => {
    setSelectedSlots((prev) =>
      prev.includes(slotId)
        ? prev.filter((id) => id !== slotId)
        : [...prev, slotId]
    );
  };

  const handleConfirm = () => {
    if (!selectedDate || selectedSlots.length === 0) {
      alert("Select a date and at least one slot");
      return;
    }

    const slotTimes = filteredSlots
      .filter((slot) => selectedSlots.includes(slot.id))
      .map((slot) => `${slot.start_time}-${slot.end_time}`);

    onConfirm({
      slotIds: selectedSlots,
      quantity: requestedPlayers,
      date: selectedDate,
      slotTimes,
    });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          {product?.productName} - Select Slots
        </Typography>

        {/* Date & Players */}
        <Box
          display="flex"
          gap={2}
          mb={2}
          mt={2}
          flexDirection={isMobile ? "column" : "row"}
        >
          <TextField
            label="Select Date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Players"
            type="number"
            inputProps={{ min: 1 }}
            value={requestedPlayers}
            onChange={(e) => setRequestedPlayers(Number(e.target.value))}
            fullWidth
          />
        </Box>

        {/* Slots */}
        {loading ? (
          <Typography>Loading slots...</Typography>
        ) : !selectedDate ? (
          <Typography>Please select a date.</Typography>
        ) : filteredSlots.length === 0 ? (
          <Typography>No slots available.</Typography>
        ) : (
          <Box
            display="flex"
            flexWrap="wrap"
            gap={1}
            maxHeight={isMobile ? "300px" : "400px"}
            overflow="auto"
          >
            {filteredSlots.map((slot) => {
              const isSelected = selectedSlots.includes(slot.id);

              return (
                <Chip
                  key={slot.id}
                  label={`${slot.start_time} - ${slot.end_time} (${slot.available_players} available)`}
                  clickable={slot.isAvailable}
                  color={isSelected ? "primary" : "default"}
                  onClick={() =>
                    slot.isAvailable && toggleSlotSelection(slot.id)
                  }
                  variant={slot.isAvailable ? "filled" : "outlined"}
                  sx={{
                    opacity: slot.isAvailable ? 1 : 0.5,
                    cursor: slot.isAvailable ? "pointer" : "not-allowed",
                    fontWeight: isSelected ? 600 : 400,
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* Actions */}
        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={selectedSlots.length === 0 || !selectedDate}
          >
            Add to Cart
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GameSlotModal;